import { graphql } from '@/gql';
import {
  type EmailSenderChoiceInput,
  type SenderChoiceField_EmailSenderChoiceFragment,
} from '@/gql/graphql';

export const SenderChoiceFieldEmailSenderChoice = graphql(`
  fragment SenderChoiceField_EmailSenderChoice on EmailSenderChoice {
    options {
      ...SenderChoiceField_EmailSender
    }
    chosen {
      ...SenderChoiceField_EmailSender
    }
  }
`);

export const SenderChoiceFieldEmailSender = graphql(`
  fragment SenderChoiceField_EmailSender on EmailSender {
    # Selected explicitly: the codegen transform meant to add __typename to
    # every document is misconfigured repo-wide, and senderInput needs it to
    # tell the three sender variants apart.
    __typename
    name
    emailAddress
    isAccountDefault
    ... on AccountEmailSender {
      id
    }
  }
`);

export type SenderChoice = SenderChoiceField_EmailSenderChoiceFragment;
export type EmailSender = SenderChoice['options'][number];

export type SenderOption = {
  key: string;
  /** The sender as a reader sees it. */
  sender: string;
  label: string;
  input: EmailSenderChoiceInput;
};

/**
 * A sender as a mail client shows it. Deliberately not the wire From header,
 * which quotes the name — those quotes are address syntax, so showing them to a
 * reader is a bug. Keep this and the backend's `formatAddress` separate.
 */
const formatSenderForDisplay = (sender: {
  name?: string | null;
  emailAddress: string;
}) => {
  const name = sender.name?.trim();
  return name ? `${name} <${sender.emailAddress}>` : sender.emailAddress;
};

const senderInput = (sender: EmailSender): EmailSenderChoiceInput => {
  switch (sender.__typename) {
    case 'UserEmailSender':
      return { own: true };
    case 'PayacaEmailSender':
      return { accountDefault: true };
    case 'AccountEmailSender':
      // The nominated address is named as accountDefault rather than by its own
      // id, so the choice follows a later change of nomination.
      return sender.isAccountDefault
        ? { accountDefault: true }
        : { accountAddressId: sender.id };
  }
};

const senderKey = (input: EmailSenderChoiceInput) => {
  if (input.own) return 'own';
  if (input.accountAddressId != null) {
    return `accountAddress:${input.accountAddressId}`;
  }
  return 'accountDefault';
};

export const toSenderOption = (sender: EmailSender): SenderOption => {
  const input = senderInput(sender);
  const formatted = formatSenderForDisplay(sender);
  return {
    key: senderKey(input),
    sender: formatted,
    label: sender.isAccountDefault
      ? `${formatted} (account default)`
      : formatted,
    input,
  };
};
